import {
  BOOK_PLAN_IDS,
  BOOK_PLANS,
  IMAGE_REPLACEMENT_PACK,
  type BookPlanId,
} from '@kitup/book-schema';
import { formatPrice, type BrandConfig } from '@kitup/brand';

/**
 * Numbers the storefront quotes, taken from where they are decided: plan
 * capabilities from `BOOK_PLANS`, prices from the brand. The copy around them
 * is written for these pages, not borrowed from the app's dictionary.
 */
export interface PlanFacts {
  readonly id: BookPlanId;
  readonly name: string;
  /** Whole dollars, e.g. `$14`: the pages quote round prices. */
  readonly price: string;
  readonly pages: number;
  readonly characters: number;
  readonly things: number;
  readonly places: number;
  readonly animated: boolean;
  readonly narration: boolean;
  readonly highResolution: boolean;
  readonly videoQuality: '720p' | '1080p' | null;
  readonly highQualityPrint: boolean;
  readonly redos: number;
}

const PLAN_NAMES: Readonly<Record<BookPlanId, string>> = {
  book: 'Book',
  live_book: 'Living Book',
  gift_book: 'Gift Book',
};

/** The plan the pages single out as the one most people pick. */
export const POPULAR_PLAN: BookPlanId = 'live_book';

/** `$14` rather than `$14.00`: a whole-dollar price reads as a price tag. */
export function wholePrice(minorUnits: number, brand: BrandConfig): string {
  const exact = formatPrice(minorUnits, brand, 'en');
  return minorUnits % 100 === 0 ? exact.replace(/\.00$/u, '') : exact;
}

export function planFacts(brand: BrandConfig): readonly PlanFacts[] {
  return BOOK_PLAN_IDS.map((id) => {
    const plan = BOOK_PLANS[id];
    return {
      id,
      name: PLAN_NAMES[id],
      price: wholePrice(brand.planPrices[id], brand),
      pages: plan.sceneCount * 2 + 2,
      characters: plan.maxCharacters,
      things: plan.maxThings,
      places: plan.maxLocations,
      animated: plan.video.enabled,
      narration: plan.narration,
      highResolution: plan.imageResolution === '2k',
      videoQuality: plan.video.enabled ? plan.video.resolution : null,
      highQualityPrint: plan.printQuality === 'high',
      redos: plan.freeImageReplacements,
    };
  });
}

export function redoPack(brand: BrandConfig): { readonly count: number; readonly price: string } {
  return { count: IMAGE_REPLACEMENT_PACK.count, price: wholePrice(brand.imagePackPrice, brand) };
}

export function cheapestPrice(brand: BrandConfig): string {
  return wholePrice(Math.min(...BOOK_PLAN_IDS.map((id) => brand.planPrices[id])), brand);
}
